import {
    TextField, Grid, Button, FormControl, InputLabel, Select, MenuItem, FormHelperText,
} from "@mui/material";
import {Controller} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useIdentityForm} from "../hooks/useIdentityForm";
import {SUFFIXES_OPTIONS} from "../../../constants/suffix";
import DatePickerInput from "../../../components/DatePickerInput";
import {useGetGenders} from "../../../hooks/useGetGenders";
import Preloader from "../../../components/Preloader";
import {formatToISO, subtractYears} from "../../../utils/dateTimeHelper";

export const IdentityForm = (props) => {
    const {data, onFinish} = props;
    const {t} = useTranslation();

    const {isLoading: isLoadingGenders, gendersOptions} = useGetGenders();

    const {
        handleSubmit, control, errors, isDirty,
    } = useIdentityForm(data);

    const onSubmit = (dataForm) => {
        const {dateOfBirth, ...formData} = dataForm;
        const dataFormatted = {
            ...formData,
            dateOfBirth: formatToISO(dateOfBirth),
        };
        onFinish?.(dataFormatted, isDirty);
    };

    return (
        <Preloader isLoading={isLoadingGenders} overflow>
            <form className="Students-form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container rowSpacing={3} columnSpacing={2}>
                    <Grid item size={{xs: 12, md: 6}}>
                        <Controller
                            name="forename"
                            control={control}
                            render={({field}) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    slotProps={{htmlInput: {maxLength: 100, required: false}}}
                                    label={t("students.identity.firstName")}
                                    placeholder={t("students.identity.placeholders.firstName")}
                                    required
                                    error={!!errors.forename}
                                    helperText={errors.forename?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <Controller
                            name="preferredName"
                            control={control}
                            render={({field}) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label={t("students.identity.preferredName")}
                                    placeholder={t("students.identity.placeholders.preferredName")}
                                    error={!!errors.preferredName}
                                    helperText={errors.preferredName?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <Controller
                            name="surname"
                            control={control}
                            render={({field}) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    slotProps={{htmlInput: {maxLength: 100, required: false}}}
                                    label={t("students.identity.lastName")}
                                    placeholder={t("students.identity.placeholders.lastName")}
                                    required
                                    error={!!errors.surname}
                                    helperText={errors.surname?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <FormControl fullWidth error={!!errors.suffix}>
                            <InputLabel>{t("students.identity.suffix")}</InputLabel>
                            <Controller
                                name="suffix"
                                control={control}
                                render={({field}) => (
                                    <Select {...field}>
                                        <MenuItem value="">
                                            <em className="select-placeholder">{t("students.identity.placeholders.suffix")}</em>
                                        </MenuItem>
                                        {SUFFIXES_OPTIONS.map((suffix) => (
                                            <MenuItem key={suffix.value} value={suffix.value}>
                                                {suffix.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.suffix?.message ? (
                                <FormHelperText>{errors.suffix.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <DatePickerInput
                            required
                            label={t("students.identity.dateOfBirth")}
                            control={control}
                            name="dateOfBirth"
                            placeholder="MM/DD/YYYY"
                            error={errors.dateOfBirth?.message}
                            minDate={subtractYears(25)}
                            maxDate={subtractYears(7)}
                        />
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <FormControl fullWidth error={!!errors.genderTypeId}>
                            <InputLabel required>{t("students.identity.gender")}</InputLabel>
                            <Controller
                                name="genderTypeId"
                                control={control}
                                render={({field}) => (
                                    <Select {...field} onChange={(event) => field.onChange(event.target.value)}>
                                        <MenuItem value="">
                                            <em className="select-placeholder">{t("students.identity.placeholders.gender")}</em>
                                        </MenuItem>
                                        {gendersOptions.map((item) => (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.genderTypeId?.message ? (
                                <FormHelperText>{errors.genderTypeId.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={12} className="w-full flex">
                        <Button
                            variant="accent"
                            type="submit"
                            className="ml-auto"
                        >
                            {t("students.addInformation")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Preloader>
    );
};
